import { getPlayerAvatarAudioSource } from "../../utils/component-utils";
import { hasActiveAudioZoneOverrideForSource } from "../../utils/audio-zone-utils";

export function resolveStableSpeakerId(store) {
  return store?.state?.authUser?.profile?.id || store?.state?.profile?.id || null;
}

export function resolveMetaverseRoomSessionId({
  app = APP,
  nafClientId = typeof NAF !== "undefined" ? NAF.clientId : null
} = {}) {
  return nafClientId || app?.hubChannel?.channel?.socket?.params?.().session_id || app?.str_session_id || null;
}

function resolveDisplayName(app, store, nafClientId) {
  const sessionId = resolveMetaverseRoomSessionId({ app, nafClientId });
  const presenceName = sessionId
    ? app?.hubChannel?.presence?.state?.[sessionId]?.metas?.[0]?.profile?.displayName
    : null;
  const profileName = store?.state?.profile?.displayName;
  return presenceName || profileName || null;
}

export function shouldRenderCaptionForMetaverseAudioZones({
  speakerRoomSessionId,
  localRoomSessionId,
  resolveAudioSourceForSessionId = getPlayerAvatarAudioSource,
  hasActiveAudioZoneOverrideForSource: hasZoneOverride = hasActiveAudioZoneOverrideForSource,
  isSpeakerSessionHidden = () => false,
  isAudioSourceLocallyMuted = () => false,
  isAudioSourceClipped = () => false
}) {
  if (!speakerRoomSessionId || !localRoomSessionId || speakerRoomSessionId === localRoomSessionId) {
    return true;
  }

  if (isSpeakerSessionHidden(speakerRoomSessionId)) {
    return false;
  }

  const audioSource = resolveAudioSourceForSessionId(speakerRoomSessionId);
  if (!audioSource) {
    return true;
  }

  if (isAudioSourceLocallyMuted(audioSource)) {
    return false;
  }

  if (hasZoneOverride(audioSource)) {
    return false;
  }

  if (isAudioSourceClipped(audioSource)) {
    return false;
  }

  return true;
}

export function createMetaverseTranscriptionHostAdapter({
  app = APP,
  store = app?.store,
  nafClientId = typeof NAF !== "undefined" ? NAF.clientId : null,
  resolveAudioSourceForSessionId = getPlayerAvatarAudioSource,
  hasActiveAudioZoneOverrideForSource: hasZoneOverride = hasActiveAudioZoneOverrideForSource,
  isSpeakerSessionHidden = sessionId => Boolean(app?.hubChannel?.isHidden?.(sessionId)),
  isAudioSourceLocallyMuted = audioSource =>
    Boolean(app?.mutedState?.has?.(audioSource)) || (app?.gainMultipliers?.get?.(audioSource) ?? 1) <= 0,
  isAudioSourceClipped
} = {}) {
  const resolveAudioSourceClipped = isAudioSourceClipped || (audioSource => Boolean(app?.clippingState?.has?.(audioSource)));

  return {
    getRoomId() {
      return app?.hub?.hub_id || null;
    },

    getLocalSpeakerIdentity() {
      const displayName = resolveDisplayName(app, store, nafClientId);
      const roomSessionId = resolveMetaverseRoomSessionId({ app, nafClientId });

      return {
        speaker: displayName,
        speakerId: resolveStableSpeakerId(store),
        speakerRoomSessionId: roomSessionId
      };
    },

    getCurrentDisplayName() {
      return resolveDisplayName(app, store, nafClientId);
    },

    shouldRenderCaption({ speakerRoomSessionId }) {
      return shouldRenderCaptionForMetaverseAudioZones({
        speakerRoomSessionId,
        localRoomSessionId: resolveMetaverseRoomSessionId({ app, nafClientId }),
        resolveAudioSourceForSessionId,
        hasActiveAudioZoneOverrideForSource: hasZoneOverride,
        isSpeakerSessionHidden,
        isAudioSourceLocallyMuted,
        isAudioSourceClipped: resolveAudioSourceClipped
      });
    }
  };
}
